/**
 * WHO-5764: the icon pages that carry a price and authenticity guide, per brand slug, with the
 * live Strapi icon name per locale. Strapi's icon-page type has no brand relation, so this static
 * map is the link between a brand listing page and its guides. Generated from Strapi; no em dashes.
 */

export type IconGuideLocale = "en" | "nl" | "de" | "fr" | "es" | "it";

export type IconGuide = {
  /** Icon page slug, the same in every locale: /<locale>/icon/<slug>. */
  slug: string;
  title: Partial<Record<IconGuideLocale, string>>;
  /**
   * WHO-6195: localized category segments of the brand x category pages that also link this
   * guide (`/merken/ligne-roset/banken` -> "banken"). The brand page itself always links every
   * guide; a brand x category page only links the guides listed for its category.
   */
  categorySegments?: string[];
  /**
   * WHO-6196: lowercase words that identify the model in a product title in any language. A
   * product of this brand whose title contains one links the guide from its "See more" row.
   */
  titleKeywords?: string[];
};

export const ICON_GUIDES_BY_BRAND: Record<string, IconGuide[]> = {
  vitra: [
    {
      slug: "eames-lounge-chair",
      title: {
        en: "Eames Lounge Chair",
        nl: "Eames Lounge Chair",
        de: "Eames Lounge Chair",
        fr: "Chaise longue Eames",
        es: "Silla Eames Lounge",
        it: "Sedia da salotto Eames",
      },
      categorySegments: [
        "armchairs",
        "fauteuils",
        "sessel",
        "sillones",
        "poltrone",
        "chairs-and-lounge-chairs",
        "stoelen-fauteuils",
        "stuhle-und-loungesessel",
        "chaises-fauteuils",
        "sillas-y-sillones",
        "sedie-e-poltrone",
      ],
      titleKeywords: [
        "lounge chair",
        "lounge-chair",
        "loungechair",
        "lounge stoel",
        "loungesessel",
      ],
    },
    {
      slug: "eames-dsw-chair",
      title: {
        en: "Eames DSW chair",
        nl: "Eames DSW-stoel",
        de: "Eames DSW Chair",
        fr: "Chaise Eames DSW",
        es: "Silla Eames DSW",
        it: "Sedia Eames DSW",
      },
    },
    {
      slug: "vitra-panton",
      title: {
        en: "Vitra Panton",
        nl: "Vitra Panton",
        de: "Vitra Panton",
        fr: "Vitra Panton",
        es: "Silla Panton de Vitra",
        it: "Sedia Panton di Vitra",
      },
    },
  ],
  usm: [
    {
      slug: "usm-haller",
      title: {
        en: "USM Haller",
        nl: "USM Haller",
        de: "USM Haller-Möbel",
        fr: "Meuble USM Haller",
        es: "Mueble USM Haller",
        it: "Mobile USM Haller",
      },
    },
  ],
  "ligne-roset": [
    {
      slug: "ligne-roset-togo",
      title: {
        en: "Ligne Roset togo",
        nl: "Ligne Roset togo",
        de: "Ligne Roset togo",
        fr: "Ligne Roset togo",
        es: "Togo de Ligne Roset",
        it: "Togo di Ligne Roset",
      },
      categorySegments: [
        "sofas",
        "banken",
        "canapes",
        "divani",
        "armchairs",
        "fauteuils",
        "sessel",
        "sillones",
        "poltrone",
        "chairs-and-lounge-chairs",
        "stoelen-fauteuils",
        "stuhle-und-loungesessel",
        "chaises-fauteuils",
        "sillas-y-sillones",
        "sedie-e-poltrone",
      ],
      titleKeywords: ["togo"],
    },
  ],
  "louis-poulsen": [
    {
      slug: "louis-poulsen-ph5",
      title: {
        en: "Louis Poulsen PH5",
        nl: "Louis Poulsen PH5",
        de: "Louis Poulsen PH5",
        fr: "Suspension Louis Poulsen PH5",
        es: "Lámpara Louis Poulsen PH5",
        it: "Lampada Louis Poulsen PH5",
      },
    },
  ],
  knoll: [
    {
      slug: "barcelona-chair",
      title: {
        en: "Barcelona chair",
        nl: "Barcelona Chair",
        de: "Barcelona Chair von Knoll kaufen?",
        fr: "Chaise Barcelona",
        es: "Sillón Barcelona",
        it: "Poltrona Barcelona",
      },
      categorySegments: [
        "armchairs",
        "fauteuils",
        "sessel",
        "sillones",
        "poltrone",
        "chairs-and-lounge-chairs",
        "stoelen-fauteuils",
        "stuhle-und-loungesessel",
        "chaises-fauteuils",
        "sillas-y-sillones",
        "sedie-e-poltrone",
      ],
      titleKeywords: ["barcelona"],
    },
    {
      slug: "tulip-table",
      title: {
        en: "Tulip table",
        nl: "Tulip-tafel",
        de: "Tulip-Tisch",
        fr: "Table Tulip",
        es: "Mesa Tulip",
        it: "Tavolo Tulip",
      },
    },
    {
      slug: "wassily-chair",
      title: {
        en: "Marcel Breuer’s Wassily Chair",
        nl: "Marcel Breuer’s Wassily Chair",
        de: "Marcel Breuer Wassily Chair",
        fr: "La chaise Wassily de Marcel Breuer",
        es: "Sillón Wassily",
        it: "Poltrona Wassily",
      },
    },
  ],
  "fritz-hansen": [
    {
      slug: "fritz-hansen-series-7",
      title: {
        en: "Fritz Hansen Series 7",
        nl: "Fritz Hansen Series 7-stoel",
        de: "Fritz Hansen Series 7-Stuhl",
        fr: "Chaise Fritz Hansen Series 7",
        es: "Silla Fritz Hansen Series 7",
        it: "Sedia Fritz Hansen Series 7",
      },
    },
    {
      slug: "egg-chair",
      title: {
        en: "Egg Chair by Fritz Hansen",
        nl: "Egg Chair Tweedehands",
        de: "Egg Chair von Fritz Hansen",
        fr: "Chaise à œuf d'occasion",
        es: "Sillón Egg",
        it: "Poltrona Egg",
      },
    },
  ],
  flos: [
    {
      slug: "arco-lamp",
      title: {
        en: "Arco Lamp",
        nl: "Arco Lamp",
        de: "Arco Lamp",
        fr: "Arco Lamp",
        es: "Lámpara Arco de Flos",
        it: "Lampada Arco di Flos",
      },
    },
  ],
  string: [
    {
      slug: "string-shelf",
      title: {
        en: "String shelf",
        nl: "String-wandrek",
        de: "String-Regal",
        fr: "Étagère String",
        es: "Estantería String",
        it: "Libreria String",
      },
    },
  ],
  artifort: [
    {
      slug: "artifort",
      title: {
        en: "Artifort",
        nl: "Artifort-meubel",
        de: "Artifort-Möbel",
        fr: "Meuble Artifort",
        es: "Mueble Artifort",
        it: "Mobile Artifort",
      },
    },
    {
      slug: "artifort-mushroom",
      title: {
        en: "Artifort Mushroom",
        nl: "Artifort Mushroom",
        de: "Artifort Mushroom",
        fr: "Fauteuil Mushroom d'Artifort",
        es: "Sillón Mushroom de Artifort",
        it: "Poltrona Mushroom di Artifort",
      },
    },
  ],
  gubi: [
    {
      slug: "gubi-beetle",
      title: {
        en: "Gubi Beetle",
        nl: "Gubi Beetle",
        de: "Gubi Beetle Chair",
        fr: "Chaise Gubi Beetle",
        es: "Silla Gubi Beetle",
        it: "Sedia Gubi Beetle",
      },
    },
  ],
  montana: [
    {
      slug: "montana",
      title: {
        en: "Montana",
        nl: "Montana-kast",
        de: "Montana-Möbel",
        fr: "Meuble Montana",
        es: "Mueble Montana",
        it: "Mobile Montana",
      },
    },
  ],
  thonet: [
    {
      slug: "thonet",
      title: {
        en: "Thonet",
        nl: "Thonet-meubel",
        de: "Thonet-Möbel",
        fr: "Meuble Thonet",
        es: "Mueble Thonet",
        it: "Mobile Thonet",
      },
    },
    {
      slug: "cesca-chair",
      title: {
        en: "Cesca chair",
        nl: "Cesca-stoel",
        de: "Cesca-Stuhl",
        fr: "Chaise Cesca",
        es: "Silla Cesca",
        it: "Sedia Cesca",
      },
    },
  ],
  leolux: [
    {
      slug: "leolux-pulla",
      title: {
        en: "Leolux Pulla",
        nl: "Leolux Pulla",
        de: "Leolux Pulla-Sofa",
        fr: "Canapé Leolux Pulla",
        es: "Sofá Leolux Pulla",
        it: "Divano Leolux Pulla",
      },
    },
  ],
  chanel: [
    {
      slug: "chanel-classic-flap",
      title: {
        en: "Chanel Classic Flap",
        nl: "Chanel Classic Flap",
        de: "Chanel Classic Flap Bag",
        fr: "Sac Chanel Classic Flap",
        es: "Bolso Chanel Classic Flap",
        it: "Borsa Chanel Classic Flap",
      },
    },
  ],
  hermes: [
    {
      slug: "hermes-kelly",
      title: {
        en: "Hermès Kelly",
        nl: "Hermès Kelly",
        de: "Hermès Kelly",
        fr: "Sac Kelly d'Hermès",
        es: "Bolso Kelly de Hermès",
        it: "Borsa Kelly di Hermès",
      },
    },
    {
      slug: "hermes-birkin",
      title: {
        en: "Hermès Birkin",
        nl: "Hermès Birkin",
        de: "Hermès Birkin",
        fr: "Sac Birkin d'Hermès",
        es: "Bolso Birkin de Hermès",
        it: "Borsa Birkin di Hermès",
      },
    },
  ],
  "de-sede": [
    {
      slug: "de-sede-ds-600",
      title: {
        en: "De Sede DS-600",
        nl: "De Sede DS-600",
        de: "De Sede DS-600-Sofa",
        fr: "Canapé De Sede DS-600",
        es: "Sofá De Sede DS-600",
        it: "Divano De Sede DS-600",
      },
    },
  ],
  pastoe: [
    {
      slug: "pastoe",
      title: {
        en: "Pastoe",
        nl: "Pastoe-meubel",
        de: "Pastoe-Möbel",
        fr: "Meuble Pastoe",
        es: "Mueble Pastoe",
        it: "Mobile Pastoe",
      },
    },
  ],
  gispen: [
    {
      slug: "gispen",
      title: {
        en: "Gispen",
        nl: "Gispen-meubel",
        de: "Gispen-Möbel",
        fr: "Meuble Gispen",
        es: "Mueble Gispen",
        it: "Mobile Gispen",
      },
    },
  ],
};
